import { useState } from "react";
import { motion } from "framer-motion";
import { brand, projectTypes, budgetRanges } from "../data/site";

const initial = {
    name: "",
    phone: "",
    email: "",
    interest: "",
    type: "",
    budget: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [sent, setSent] = useState(false);

    const onChange = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        const msg = [
            "*New Enquiry — BRA Sheriff Builders*",
            "",
            `• Name: ${form.name || "—"}`,
            `• Phone: ${form.phone || "—"}`,
            `• Email: ${form.email || "—"}`,
            `• Interested In: ${form.interest || "—"}`,
            `• Project Type: ${form.type || "—"}`,
            `• Budget: ${form.budget || "—"}`,
            "",
            `Message:`,
            `${form.message || "—"}`,
        ].join("\n");
        const url = `https://wa.me/${brand.phoneRaw}?text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        setSent(true);
        setTimeout(() => setSent(false), 4000);
    };

    const inputCls =
        "w-full bg-transparent border-b border-[rgba(212,175,55,0.35)] px-0 py-3 text-white placeholder:text-[#7a7a7a] text-sm md:text-base font-light focus:outline-none focus:border-[#E2C56D] transition-colors";

    const labelCls =
        "text-[10px] uppercase tracking-[0.32em] text-[#C89B3C] mb-2 block";

    return (
        <section
            id="contact"
            className="relative bg-[#0b0b0b] py-24 md:py-40 px-6 md:px-10 border-t border-[rgba(212,175,55,0.2)]"
            data-testid="contact-section"
        >
            <div className="mx-auto max-w-[1440px]">
                <div className="flex items-center gap-6 mb-10">
                    <span className="w-16 h-px bg-[#C89B3C]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                        Chapter 16 — Begin
                    </span>
                </div>

                <div className="grid grid-cols-12 gap-6 items-end mb-14">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9 }}
                        className="col-span-12 lg:col-span-8 font-display font-light text-white text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                    >
                        Let&apos;s build
                        <br />
                        something <em className="text-[#E2C56D]">lasting.</em>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="col-span-12 lg:col-span-4 text-[#bebebe] text-base leading-relaxed font-light"
                    >
                        Share a few details — we&apos;ll reach out with a considered response
                        within one business day.
                    </motion.p>
                </div>

                <div className="grid grid-cols-12 gap-6 lg:gap-14">
                    {/* Form */}
                    <motion.form
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9 }}
                        onSubmit={onSubmit}
                        className="col-span-12 lg:col-span-7 border border-[rgba(212,175,55,0.25)] p-8 md:p-10 bg-[#0b0b0b]/70"
                        data-testid="contact-form"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <label className={labelCls} htmlFor="name">
                                    Name
                                </label>
                                <input
                                    id="name"
                                    className={inputCls}
                                    placeholder="Your full name"
                                    value={form.name}
                                    onChange={onChange("name")}
                                    required
                                    data-testid="contact-name"
                                />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="phone">
                                    Phone
                                </label>
                                <input
                                    id="phone"
                                    className={inputCls}
                                    placeholder="+91 ______ ____"
                                    value={form.phone}
                                    onChange={onChange("phone")}
                                    required
                                    data-testid="contact-phone"
                                />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="email">
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    className={inputCls}
                                    placeholder="you@domain.com"
                                    value={form.email}
                                    onChange={onChange("email")}
                                    data-testid="contact-email"
                                />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="interest">
                                    Interested In
                                </label>
                                <input
                                    id="interest"
                                    className={inputCls}
                                    placeholder="Sheriff Aria, or a project name"
                                    value={form.interest}
                                    onChange={onChange("interest")}
                                    data-testid="contact-interest"
                                />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="type">
                                    Type of Project
                                </label>
                                <select
                                    id="type"
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={form.type}
                                    onChange={onChange("type")}
                                    data-testid="contact-type"
                                >
                                    <option value="" className="bg-[#0b0b0b]">
                                        Select
                                    </option>
                                    {projectTypes.map((t) => (
                                        <option key={t} value={t} className="bg-[#0b0b0b]">
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="budget">
                                    Budget
                                </label>
                                <select
                                    id="budget"
                                    className={`${inputCls} appearance-none pr-8`}
                                    value={form.budget}
                                    onChange={onChange("budget")}
                                    data-testid="contact-budget"
                                >
                                    <option value="" className="bg-[#0b0b0b]">
                                        Select
                                    </option>
                                    {budgetRanges.map((t) => (
                                        <option key={t} value={t} className="bg-[#0b0b0b]">
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelCls} htmlFor="message">
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    rows={3}
                                    className={inputCls + " resize-none"}
                                    placeholder="Tell us a little about what you're looking for…"
                                    value={form.message}
                                    onChange={onChange("message")}
                                    data-testid="contact-message"
                                />
                            </div>
                        </div>

                        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-[#7a7a7a]">
                                Your enquiry opens WhatsApp — no data stored on our end.
                            </p>
                            <button
                                type="submit"
                                className="btn-sheen inline-flex items-center gap-3 px-7 py-4 bg-[#C89B3C] text-[#0b0b0b] text-[11px] uppercase tracking-[0.32em] font-medium hover:bg-[#E2C56D] transition-colors"
                                data-testid="contact-submit"
                            >
                                <span className="relative z-[2]">
                                    {sent ? "WhatsApp Opened ✓" : "Schedule Consultation"}
                                </span>
                                <span className="relative z-[2]">→</span>
                            </button>
                        </div>
                    </motion.form>

                    {/* Office / map */}
                    <motion.aside
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="col-span-12 lg:col-span-5"
                        data-testid="contact-office"
                    >
                        <div className="border border-[rgba(212,175,55,0.25)] overflow-hidden">
                            <div className="relative aspect-[4/3]">
                                <iframe
                                    title="BRA Sheriff Office Location"
                                    src="https://www.google.com/maps?q=Langford+Road+Shanti+Nagar+Bangalore&output=embed"
                                    className="w-full h-full grayscale contrast-125 opacity-90"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                                <div className="absolute inset-0 pointer-events-none border border-[rgba(212,175,55,0.4)]" />
                                <div className="absolute inset-0 pointer-events-none bg-[#0b0b0b]/20 mix-blend-multiply" />
                            </div>
                            <div className="p-8">
                                <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C]">
                                    Head Office
                                </div>
                                <div className="mt-3 font-display text-2xl text-white leading-tight">
                                    {brand.fullName}
                                </div>
                                <address className="not-italic mt-4 text-[#bebebe] text-sm font-light leading-relaxed">
                                    {brand.address.line1}
                                    <br />
                                    {brand.address.line2}
                                    <br />
                                    {brand.address.line3}
                                </address>

                                <div className="mt-6 space-y-3 text-sm">
                                    <div className="flex justify-between gap-6 border-b border-[rgba(212,175,55,0.18)] pb-2">
                                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#7a7a7a]">
                                            Phone
                                        </span>
                                        <a
                                            href={`tel:${brand.phoneRaw}`}
                                            className="text-white hover:text-[#E2C56D]"
                                            data-testid="office-phone"
                                        >
                                            {brand.phone}
                                        </a>
                                    </div>
                                    <div className="flex justify-between gap-6 border-b border-[rgba(212,175,55,0.18)] pb-2">
                                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#7a7a7a]">
                                            Email
                                        </span>
                                        <a
                                            href={`mailto:${brand.email}`}
                                            className="text-white hover:text-[#E2C56D]"
                                        >
                                            {brand.email}
                                        </a>
                                    </div>
                                    <div className="flex justify-between gap-6">
                                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#7a7a7a]">
                                            Hours
                                        </span>
                                        <span className="text-white">{brand.hours}</span>
                                    </div>
                                </div>

                                <a
                                    href="https://www.google.com/maps?q=Langford+Road+Shanti+Nagar+Bangalore"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#C89B3C] hover:text-[#E2C56D]"
                                    data-testid="open-maps"
                                >
                                    Open in Google Maps
                                    <span className="w-8 h-px bg-[#C89B3C]" />
                                </a>
                            </div>
                        </div>
                    </motion.aside>
                </div>
            </div>
        </section>
    );
}
